<template>
    <Dialog v-model:visible="visible" modal :header="`Add New ${siteTitle}`" :style="{ width: '50vw' }" class="p-fluid">
        <div class="p-6">
            <div class="grid grid-cols-1 gap-6">
                <div class="field">
                    <div class="flex items-center gap-2 mb-2">
                        <i class="pi pi-map-marker text-sm"></i>
                        <label for="country" class="block text-sm font-medium">Country</label>
                    </div>
                    <Select filter v-model="formData.country" :options="sortedCountries" optionLabel="name"
                        optionValue="code" placeholder="Select a country" class="w-full" fluid>
                        <template #option="slotProps">
                            <div class="flex items-center justify-between w-full py-1">
                                <div class="flex items-center">
                                    <span :class="`fi fi-${slotProps.option.code.toLowerCase()}`" class="mr-3"></span>
                                    <div class="text-sm">{{ slotProps.option.name }}</div>
                                </div>
                                <span v-if="markets.includes(slotProps.option.code)"
                                    class="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    Available Deals
                                </span>
                            </div>
                        </template>
                    </Select>
                </div>
                <div class="field">
                    <div class="flex items-center gap-2 mb-2">
                        <i class="pi pi-tag text-sm"></i>
                        <label for="type" class="block text-sm font-medium">Type</label>
                    </div>
                    <Select v-model="formData.type" 
                            :options="filteredSiteTypes" 
                            optionLabel="name" 
                            optionValue="code"
                            placeholder="Select a type"
                            class="w-full" 
                            fluid />
                </div>
                <div class="field">
                    <div class="flex items-center gap-2 mb-2">
                        <i class="pi pi-bookmark text-sm"></i>
                        <label for="name" class="block text-sm font-medium">{{ siteTitle }}</label>
                    </div>
                    <InputText id="name" v-model="formData.name" :placeholder="`Enter ${siteTitle.toLowerCase()} name`"
                        class="w-full" />
                </div>
                <div class="field" v-if="props.isSeo">
                    <div class="flex items-center gap-2 mb-2">
                        <i class="pi pi-globe text-sm"></i>
                        <label for="domain" class="block text-sm font-medium">Domain</label>
                    </div>
                    <InputText id="domain" v-model="formData.domain" placeholder="Enter domain name" class="w-full" />
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-3 px-6 pb-4">
                <Button label="Cancel" icon="pi pi-times" @click="closeModal" text class="hover:bg-gray-100" />
                <Button label="Save" icon="pi pi-check" @click="handleSave" severity="primary" :disabled="!isFormValid" />
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import { getSiteTypes } from '@/utils/siteTypes';
import countries from '@/utils/countries';
import { markets } from '@/utils/markets';
import { useToast } from "primevue/usetoast";
import SitesService from '@/services/SitesService';
const props = defineProps({
    sites: {
        type: Array,
        required: true,
        default: () => []
    },
    userId: {
        type: String,
        required: true
    },
    isSeo: {
        type: Boolean,
        required: true
    }
});

console.log('Props', props);
const visible = ref(false);
const formData = ref({
    name: '',
    type: '',
    domain: '',
    country: ''
});

const siteTitle = computed(() => {
    return props.isSeo ? 'Site' : 'Toplist'
});

const siteTypes = getSiteTypes();

const sortedCountries = computed(() => {
    return countries.value.sort((a, b) => {
        // Check if countries are in markets
        const aInMarkets = markets.includes(a.code);
        const bInMarkets = markets.includes(b.code);

        // If one is in markets and other isn't, prioritize the one in markets
        if (aInMarkets && !bInMarkets) return -1;
        if (!aInMarkets && bInMarkets) return 1;

        // If both are in markets or both aren't, sort alphabetically
        return a.name.localeCompare(b.name);
    });
});

const isNameAutogenerated = ref(true); // Track if name is auto-generated

const generateSiteName = (countryCode) => {
    const baseName = `New Toplist - ${countryCode}`;

    if (!props.sites.some(site => site.name === baseName)) {
        return baseName;
    }

    let counter = 2;
    let newName;
    do {
        newName = `${baseName} ${counter}`;
        counter++;
    } while (props.sites.some(site => site.name === newName));

    return newName;
};

// Watch for name changes by user
watch(() => formData.value.name, (newValue, oldValue) => {
    // If the value changes and it's not from our auto-generation
    if (newValue !== oldValue && formData.value.country && formData.value.type) {
        isNameAutogenerated.value = false;
    }
});

// Watch for both country and type selection
watch(
    [() => formData.value.country, () => formData.value.type],
    ([newCountry, newType]) => {
        // Only auto-generate if name hasn't been manually set
        if (newCountry && newType && isNameAutogenerated.value) {
            formData.value.name = generateSiteName(newCountry);
        }
    }
);

const filteredSiteTypes = computed(() => {

    if(props.isSeo) {
        formData.value.type = 'seo';
        return getSiteTypes().filter(type => type.code === 'seo');
    } else {
        // If not SEO, show all types except SEO
        return getSiteTypes().filter(type => type.code !== 'seo');
    }
    
});

// Update watcher to use props.isSeo
watch(() => props.isSeo, (newValue) => {
    if (newValue) {
        // If switching to SEO, force type to 'seo'
        formData.value.type = 'seo';
    } else {
        // If switching from SEO, clear the type
        formData.value.type = '';
    }
});

const emit = defineEmits(['close', 'site-added']);

const closeModal = () => {
    formData.value = {
        name: '',
        type: '',
        domain: '',
        country: ''
    };
    isNameAutogenerated.value = true;
    visible.value = false;
    emit('close');
};

const toast = useToast();

const handleSave =  async () => {
    try {
        console.log('Saving site...', formData.value);
        let body = {
            userId: props.userId,
            name: formData.value.name,
            userType: props.isSeo ? 'seo' : 'other',
            type: formData.value.type,
            domain: formData.value.domain,
            country: formData.value.country
        };
        console.log('Body', body);
        const response = await SitesService.addSiteByAdmin(body);
        console.log('Site saved', response.data);
        emit('site-added', response.data);
        closeModal();
    } catch (error) {
        console.error('Error saving site', error);
        toast.add({
            severity: 'error',
            summary: 'Error saving site',
            detail: error.response?.data || 'An unexpected error occurred',
            life: 3000
        });
    } finally {
    }
   
};

const open = () => {
    visible.value = true;
    console.log('Sites', props.sites);
};

const isFormValid = computed(() => {
    const baseValidation = formData.value.name && 
                          formData.value.type && 
                          formData.value.country;
                          
    if (props.isSeo) {
        return baseValidation && formData.value.domain;
    }
    
    return baseValidation;
});

// Expose the open method to parent components
defineExpose({ open });
</script>